import { useEffect, useState } from 'react'
import {
  BagLineIcon,
  CurecallBanner,
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import SectionTitle from 'src/components/section-title/section-title.component'
import { TFollowup } from 'src/types/followup'
import GenericFollowupFormComponent from 'src/components/followup/generic-followup-form.component'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useSearchParams } from 'react-router-dom'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useTranslation } from 'react-i18next'
import FollowupCardList from 'src/components/followup-card-list/followup-card-list.component'

export type Props = {
  patient: TPatient
}

const AddFollowup = ({ patient }: Props) => {
  const { getFollowupById, project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const followupIdToAdd = Number(searchParams.get('followupId'))

  const [selectedFollowup, setSelectedFollowup] = useState<TFollowup>()
  const [showFollowupForm, setShowFollowupForm] = useState(!!followupIdToAdd)

  useEffect(() => {
    if (followupIdToAdd) {
      const tmpSelectedFollowup = getFollowupById(followupIdToAdd)
      setSelectedFollowup(tmpSelectedFollowup as TFollowup)
      setShowFollowupForm(true)
    }
  }, [followupIdToAdd, getFollowupById])

  const handleCancelAddFollowup = () => {
    const hasPatientFollowups = patient.PatientFollowups && patient.PatientFollowups?.length > 0
    hasPatientFollowups
      ? navigate(`/${project.id}/medical/patients/${patient.id}`)
      : setShowFollowupForm(false)
  }

  const handleConfigureSelectedService = () => {
    setShowFollowupForm(true)
    if (selectedFollowup) {
      setSearchParams({ followupId: `${selectedFollowup.id}` })
    }
  }

  if (showFollowupForm && selectedFollowup) {
    if (!selectedFollowup.configUI || selectedFollowup?.configUI?.medEventsUI?.length < 1) {
      console.warn(`Missing configUI for followupId: ${selectedFollowup.id}`, selectedFollowup)
      return (
        <div className="c-pt-10">
          <CurecallBanner appearance="ERROR">
            {t('pages.patient-page.add-followup.impossible-configuration-please-contact-curecall')}
          </CurecallBanner>
        </div>
      )
    }
    return (
      <div className="c-mt-3 c-bg-white">
        <GenericFollowupFormComponent
          followup={selectedFollowup}
          patient={patient}
          onCancel={handleCancelAddFollowup}
        />
      </div>
    )
  }

  return (
    <div className="c-flex c-flex-col c-gap-4 c-mt-4">
      <SectionTitle
        icon={<BagLineIcon className="c-w-6" />}
        sectionTitle={t('components.patient.add-or-edit.add-new-patient.section-title')}
      />

      <FollowupCardList selectedFollowup={selectedFollowup} onClickOnCard={setSelectedFollowup} />

      <div className="c-mt-2 c-flex c-items-center c-justify-center c-gap-4 c-pb-10">
        {patient.PatientFollowups && patient.PatientFollowups.length > 0 && (
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            size={ECurecallSize.SMALL}
            onClick={handleCancelAddFollowup}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
        )}
        <CurecallButton
          appearance={ECurecallButtonAppearance.PRIMARY}
          size={ECurecallSize.SMALL}
          onClick={handleConfigureSelectedService}
          isDisabled={!selectedFollowup}
        >
          {t('pages.patient-page.add-followup.configure-followup')}
        </CurecallButton>
      </div>
    </div>
  )
}

export default AddFollowup
